import Image from 'next/image';
import { motion } from 'framer-motion';

/**
 * CommerceSection — "Custom gets configured, not picked."
 *
 * Layout:
 *   Desktop (lg+): two columns — copy left, live-storefront mockup right.
 *   Tablet/Mobile: stacked — copy first, mockup below, features as 2x2 grid.
 *
 * The storefront mockup is built in JSX (not a static image) so it stays
 * pixel-crisp, themable via jade tokens, and lightweight to ship.
 */

function Icon({ children, className }) {
  return (
    <svg
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth={1.5}
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
      className={className}
    >
      {children}
    </svg>
  );
}

const StorefrontIcon = (p) => (
  <Icon {...p}>
    <path d='M3 9l1.5-5h15L21 9' />
    <path d='M3 9v11h18V9' />
    <path d='M3 9c0 1.7 1.3 3 3 3s3-1.3 3-3 1.3 3 3 3 3-1.3 3-3 1.3 3 3 3 3-1.3 3-3' />
    <path d='M9 20v-5h6v5' />
  </Icon>
);

const FormulaIcon = (p) => (
  <Icon {...p}>
    <rect x='3' y='3' width='18' height='18' rx='2' />
    <path d='M7 8h10M7 13h4M7 17h4M14 14l4 4M18 14l-4 4' />
  </Icon>
);

const CubeIcon = (p) => (
  <Icon {...p}>
    <path d='M12 3l9 5v8l-9 5-9-5V8z' />
    <path d='M3 8l9 5 9-5M12 13v10' />
  </Icon>
);

const QuoteOrderIcon = (p) => (
  <Icon {...p}>
    <path d='M5 4h11l4 4v12H5z' />
    <path d='M16 4v4h4' />
    <path d='M9 13h6M9 17h4' />
  </Icon>
);

const ArrowRight = (p) => (
  <Icon {...p}>
    <path d='M5 12h14M13 6l6 6-6 6' />
  </Icon>
);

const FEATURES = [
  { label: 'Configurable storefronts', Icon: StorefrontIcon },
  { label: 'Formula pricing', Icon: FormulaIcon },
  { label: '3D visualization', Icon: CubeIcon },
  { label: 'Quote-to-order workflows', Icon: QuoteOrderIcon },
];

/**
 * @param {Object} props
 * @param {'full'|'compact'} [props.mediaScale='full']
 *   'full'    — homepage treatment: the mockup is the section hero. Narrow copy
 *               column and a +4% origin-right overscale so the capture grows
 *               leftward into the gutter and bleeds toward the viewport edge.
 *   'compact' — /launch treatment: same capture, no overscale, and a more even
 *               column split. On /launch this section is one beat in a long
 *               argument rather than the page's centerpiece, and at full scale
 *               the mockup swamped the copy beside it.
 */
export function CommerceSection({ mediaScale = 'full' }) {
  const isCompact = mediaScale === 'compact';

  return (
    <section
      id='commerce'
      className='relative overflow-hidden bg-[#0a1612] py-16 md:py-24'
    >
      {/* Top hairline divider — seals the seam against the preceding
          WhyTimbercloudExists section (both used bg-zinc-950 before, so
          the boundary read as nothing). */}
      <div
        aria-hidden='true'
        className='absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-jade-soft/30 to-transparent'
      />
      {/* Ambient background glow — jade pool top-right behind the mockup */}
      <div aria-hidden='true' className='absolute inset-0 pointer-events-none'>
        <div className='absolute top-0 inset-x-0 h-40 bg-gradient-to-b from-jade-deep/[0.08] to-transparent' />
        <div className='absolute right-0 top-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-jade-deep/[0.07] blur-3xl' />
        <div className='absolute left-1/4 bottom-0 w-[500px] h-[500px] rounded-full bg-jade-deep/[0.05] blur-3xl' />
      </div>

      <div className='tc-container relative z-10'>
        <div
          className={`grid gap-10 lg:gap-16 xl:gap-20 items-center ${
            isCompact ? 'lg:grid-cols-[5fr_6fr]' : 'lg:grid-cols-[4fr_7fr]'
          }`}
        >
          {/* === Left column: copy === */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
            className={
              isCompact
                ? 'lg:pr-4 lg:max-w-[520px] xl:max-w-[560px]'
                : 'lg:pr-4 lg:max-w-[440px] xl:max-w-[480px]'
            }
          >
            <span className='inline-block px-3 py-1 rounded-full text-[11px] font-sans tracking-[0.25em] uppercase border bg-jade-deep/15 text-jade-soft border-jade-deep/30 mb-5'>
              Commerce
            </span>
            <h2 className='text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1] font-display text-white [text-wrap:balance]'>
              Custom gets{' '}
              <span className='text-jade-soft'>configured,</span> not picked.
            </h2>
            <p className='mt-4 text-base md:text-lg text-zinc-300 leading-relaxed'>
              Traditional ecommerce was built for fixed SKUs.{' '}
              <span className='font-semibold text-jade-soft'>TimberCloud</span>{' '}
              turns your catalog into a configurable storefront where customers
              enter dimensions, choose materials, finishes, and options, see
              calculated pricing, and submit a complete made-to-order purchase
              without the usual quote-and-email back-and-forth.
            </p>
            <p className='mt-4 text-sm md:text-base text-zinc-400 leading-relaxed'>
              Sell direct or give dealers, contractors, designers, and trade
              customers a faster way to configure and place orders online.
            </p>

            {/* Feature grid — icon + title, no chrome */}
            <div className='mt-7 grid grid-cols-2 lg:grid-cols-4 gap-5'>
              {FEATURES.map(({ label, Icon: IconComp }, i) => (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.4, delay: 0.2 + i * 0.06 }}
                  className='flex flex-col items-start gap-2'
                >
                  <IconComp className='h-8 w-8 md:h-9 md:w-9 text-jade-soft' />
                  <span className='text-[12px] font-semibold text-white leading-[1.2] [hyphens:none]'>
                    {label}
                  </span>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <a
              href='/product/commerce'
              className='group mt-7 inline-flex items-center gap-2 text-jade-soft hover:text-jade font-semibold text-sm transition-colors duration-200'
            >
              See Commerce
              <ArrowRight className='h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5' />
            </a>
          </motion.div>

          {/* === Right column: configurable item mock (image) ===
              Outer motion.div owns the mount fade/scale animation.
              Inner static div owns the +10% lg-scale (origin-right) so the
              image grows leftward into the gap and becomes the section hero
              without colliding with framer-motion's transform. */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className='relative w-full'
          >
            <div
              className={`relative w-full transform-gpu ${
                isCompact ? '' : 'lg:scale-[1.04] lg:origin-right'
              }`}
            >
              {/* Subtle matte frame — thin gradient padding + hairline ring
                  so the dark mockup separates from the dark section bg. */}
              <div className='relative rounded-[20px] p-1.5 md:p-2 bg-gradient-to-br from-white/[0.07] via-white/[0.025] to-white/[0.04] ring-1 ring-white/[0.08] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]'>
                {/* Compact crops the capture to end just under the last product
                    row — the source PNG runs on for another ~450px of empty
                    catalog and the "Add product" tile, which reads as dead space
                    beside the copy. Cropped here in CSS rather than in the file:
                    catalog-management.png is also the homepage mockup and a
                    LaunchHero reel slide, and both want the full frame. */}
                <div
                  className={
                    isCompact
                      ? 'relative w-full overflow-hidden rounded-2xl aspect-[2000/1590]'
                      : 'contents'
                  }
                >
                  <Image
                    src='/screenshots/catalog-management.png'
                    alt='Configurable product storefront preview'
                    width={2000}
                    height={2050}
                    priority={false}
                    className={
                      isCompact
                        ? 'absolute inset-0 h-full w-full object-cover object-top'
                        : 'block w-full h-auto rounded-2xl'
                    }
                  />
                </div>
              </div>
              <OperationsStatusCard />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/**
 * OperationsStatusCard — small floating pill hanging above + right of
 * the storefront mockup. Suggests the configurator hands the order
 * straight to production. Sits outside the image bounds so it never
 * collides with the screenshot's own UI text.
 *
 * - Subtle jade glow at rest, brighter on hover.
 * - Pulsing live-dot status indicator on the right.
 */
function OperationsStatusCard() {
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.6, delay: 0.9 }}
      whileHover={{ y: -2, scale: 1.02 }}
      className='group absolute top-2 right-2 md:top-2.5 md:right-3 lg:top-3 lg:right-4 z-10 rounded-full bg-jade-deep/15 hover:bg-jade-deep/25 backdrop-blur-md ring-1 ring-jade-soft/25 hover:ring-jade-soft/50 shadow-[0_0_12px_-10px_rgba(52,211,153,0.3)] hover:shadow-[0_0_18px_-6px_rgba(52,211,153,0.5)] transition-all duration-300 ease-out px-2 py-0.5 md:px-2.5 md:py-1'
    >
      <div className='flex items-center gap-1 md:gap-1.5'>
        <svg
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth={2.5}
          strokeLinecap='round'
          strokeLinejoin='round'
          aria-hidden='true'
          className='h-2 w-2 md:h-2.5 md:w-2.5 text-jade-soft shrink-0'
        >
          <path d='M5 12.5l4 4 10-10' />
        </svg>
        <span className='text-[8px] md:text-[9px] text-jade-soft font-medium leading-tight whitespace-nowrap'>
          Order synced to production
        </span>
        {/* Live status indicator — pulsing dot with a softer halo */}
        <span aria-hidden='true' className='relative inline-flex h-1 w-1 md:h-1.5 md:w-1.5 ml-0.5'>
          <span className='absolute inset-0 rounded-full bg-jade-soft/60 animate-ping' />
          <span className='relative inline-flex h-1 w-1 md:h-1.5 md:w-1.5 rounded-full bg-jade-soft shadow-[0_0_5px_rgba(52,211,153,0.8)]' />
        </span>
      </div>
    </motion.div>
  );
}

export default CommerceSection;
